import { useState } from 'react';
import type { Repository, Site, Project } from './types';
import { about } from './data';
import { EditModeProvider, useEditMode } from './components/EditModeProvider';
import PinModal from './components/PinModal';
import EditableSiteCard, { AddSiteButton } from './components/EditableSiteCard';
import EditableProjectCard, { AddProjectButton } from './components/EditableProjectCard';
import EditableRepoCard, { AddRepoButton } from './components/EditableRepoCard';
import { Github, Edit3, LogOut } from 'lucide-react';

function Header() {
  return (
    <header className="header">
      <div className="identity">
        <div className="logo">
          <span className="logo-dot" />
          <span className="logo-dot" />
          <span className="logo-dot" />
        </div>
        <h1 className="name">HAJIN</h1>
        <p className="tagline">{Array.isArray(about.role) ? about.role.join(' / ') : about.role} • {about.status}</p>
      </div>
    </header>
  );
}

function Navigation() {
  const { isEditMode } = useEditMode();
  
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className={`nav ${isEditMode ? 'edit-mode' : ''}`}>
      <a href="#about" className="nav-link" onClick={(e) => { e.preventDefault(); scrollTo('about'); }}>About</a>
      <a href="#sites" className="nav-link" onClick={(e) => { e.preventDefault(); scrollTo('sites'); }}>Sites</a>
      <a href="#repos" className="nav-link" onClick={(e) => { e.preventDefault(); scrollTo('repos'); }}>Repos</a>
      <a href="#projects" className="nav-link" onClick={(e) => { e.preventDefault(); scrollTo('projects'); }}>Projects</a>
    </nav>
  );
}

function AboutSection() {
  return (
    <section id="about" className="section">
      <div className="section-header">
        <span className="section-number">00</span>
        <h2 className="section-title">About</h2>
      </div>
      <div className="about-grid">
        <div className="about-item">
          <span className="about-label">Status</span>
          <span className="about-value">{about.status}</span>
        </div>
        <div className="about-item">
          <span className="about-label">Role</span>
          <span className="about-value">{Array.isArray(about.role) ? about.role.join(' / ') : about.role}</span>
        </div>
        <div className="about-item">
          <span className="about-label">Languages</span>
          <div className="about-tags">
            {about.languages.map(lang => (
              <span key={lang} className="about-tag">{lang}</span>
            ))}
          </div>
        </div>
        <div className="about-item">
          <span className="about-label">Skills</span>
          <div className="about-tags">
            {about.skills.map(skill => (
              <span key={skill} className="about-tag">{skill}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SitesSection() {
  const { isEditMode, sites, updateSites } = useEditMode();
  const [showAddForm, setShowAddForm] = useState(false);

  const handleSaveSite = (site: Site) => {
    if (showAddForm) {
      updateSites([...sites, site]);
      setShowAddForm(false);
    } else {
      updateSites(sites.map(s => s.id === site.id ? site : s));
    }
  };

  const handleDeleteSite = (id: string) => {
    updateSites(sites.filter(s => s.id !== id));
  };

  return (
    <section id="sites" className={`section ${isEditMode ? 'edit-mode' : ''}`}>
      <div className="section-header">
        <span className="section-number">01</span>
        <h2 className="section-title">Sites</h2>
      </div>
      <div className="cards">
        {sites.map(site => (
          <EditableSiteCard 
            key={site.id} 
            site={site} 
            isEditMode={isEditMode}
            onSave={handleSaveSite}
            onDelete={handleDeleteSite}
          />
        ))}
        {isEditMode && (
          showAddForm ? (
            <EditableSiteCard 
              isAddNew 
              isEditMode={isEditMode}
              onSave={handleSaveSite}
              onCancel={() => setShowAddForm(false)}
            />
          ) : (
            <AddSiteButton onClick={() => setShowAddForm(true)} />
          )
        )}
      </div>
    </section>
  );
}

function ReposSection() {
  const { isEditMode, customRepos, updateCustomRepos } = useEditMode();
  const [showAddForm, setShowAddForm] = useState(false);

  const handleSaveRepo = (repo: Repository) => {
    if (showAddForm) {
      updateCustomRepos([...customRepos, repo]);
      setShowAddForm(false);
    } else {
      updateCustomRepos(customRepos.map(r => r.id === repo.id ? repo : r));
    }
  };

  const handleDeleteRepo = (id: number) => {
    updateCustomRepos(customRepos.filter(r => r.id !== id));
  };

  return (
    <section id="repos" className={`section ${isEditMode ? 'edit-mode' : ''}`}>
      <div className="section-header">
        <span className="section-number">02</span>
        <h2 className="section-title">Repos</h2>
        <a href="https://github.com/husky-696" target="_blank" rel="noopener noreferrer" className="github-header-link">
          <Github size={14} /> GitHub Profile
        </a>
      </div>
      <div className="cards">
        {customRepos.map(repo => (
          <EditableRepoCard
            key={repo.id}
            repo={repo}
            isEditMode={isEditMode}
            onSave={handleSaveRepo}
            onDelete={handleDeleteRepo}
          />
        ))}
        {isEditMode && (
          showAddForm ? (
            <EditableRepoCard
              isAddNew
              isEditMode={isEditMode}
              onSave={handleSaveRepo}
              onCancel={() => setShowAddForm(false)}
            />
          ) : (
            <AddRepoButton onClick={() => setShowAddForm(true)} />
          )
        )}
        {customRepos.length === 0 && !isEditMode && (
          <div className="card">
            <div className="card-content">
              <p className="card-description">No repositories to display. Add some in edit mode!</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ProjectsSection() {
  const { isEditMode, projects, updateProjects } = useEditMode();
  const [showAddForm, setShowAddForm] = useState(false);

  const handleSaveProject = (project: Project) => {
    if (showAddForm) {
      updateProjects([...projects, project]);
      setShowAddForm(false);
    } else {
      updateProjects(projects.map(p => p.id === project.id ? project : p));
    }
  };

  const handleDeleteProject = (id: string) => {
    updateProjects(projects.filter(p => p.id !== id));
  };

  return (
    <section id="projects" className={`section ${isEditMode ? 'edit-mode' : ''}`}>
      <div className="section-header">
        <span className="section-number">03</span>
        <h2 className="section-title">Projects</h2>
      </div>
      <div className="cards">
        {projects.map(project => (
          <EditableProjectCard
            key={project.id}
            project={project}
            isEditMode={isEditMode}
            onSave={handleSaveProject}
            onDelete={handleDeleteProject}
          />
        ))}
        {isEditMode && (
          showAddForm ? (
            <EditableProjectCard
              isAddNew
              isEditMode={isEditMode}
              onSave={handleSaveProject}
              onCancel={() => setShowAddForm(false)}
            />
          ) : (
            <AddProjectButton onClick={() => setShowAddForm(true)} />
          )
        )}
      </div>
    </section>
  );
}

function Footer() {
  const { isEditMode, openPinModal, logout } = useEditMode();
  
  return (
    <footer className="footer">
      <p>© 2024 {about.name}</p>
      <button 
        onClick={isEditMode ? logout : openPinModal} 
        className={`edit-toggle-btn ${isEditMode ? 'active' : ''}`}
      >
        {isEditMode ? (
          <><LogOut size={14} /> Exit Edit</>
        ) : (
          <><Edit3 size={14} /> Edit</>
        )}
      </button>
    </footer>
  );
}

function AppContent() {
  const { showPinModal, closePinModal } = useEditMode();

  return (
    <>
      <div className="dot-pattern" />
      <div className="container">
        <Header />
        <Navigation />
        <main className="main">
          <AboutSection />
          <SitesSection />
          <ReposSection />
          <ProjectsSection />
        </main>
        <Footer />
      </div>
      <PinModal isOpen={showPinModal} onClose={closePinModal} />
    </>
  );
}

function App() {
  return (
    <EditModeProvider>
      <AppContent />
    </EditModeProvider>
  );
}

export default App;
